import Theme from './model.js'
import { deepGet, addToCollection, convertNumberToLocale } from '../shared/helper'

const initialCodeState = {
  id: null,
  code: '',
  release: false
}

const initialJsonState = {
  id: null,
  json: ''
}

const initialCssState = {
  id: null,
  css: '',
  onlyFrontend: false,
  name: ''
}

const initialTextState = {
  id: null,
  text: ''
}

const initialFileState = {
  id: null,
  name: '',
  path: null
}

export default {
  namespaced: true,
  state: {
    themes: [],
    usage: [],
    filteredCollection: [],
    current: new Theme(),
    currentCode: initialCodeState,
    currentJson: initialJsonState,
    currentCss: initialCssState,
    currentText: initialTextState,
    files: [],
    cssFiles: [],
    currentFile: initialFileState
  },
  getters: {
    getCollection: state => state.themes,
    getFilteredCollection: state => state.filteredCollection,
    getById: state => (id) => state.themes.find(theme => theme.id === id),
    getCurrent: state => state.current,
    getCurrentCode: state => state.currentCode,
    getCurrentJson: state => state.currentJson,
    getCurrentCss: state => state.currentCss,
    getCurrentText: state => state.currentText,
    getFiles: state => state.files,
    getCssFiles: state => state.cssFiles,
    getCurrentFile: state => state.currentFile,
    getUsage: state => state.usage
  },
  actions: {
    getCollection({ commit, getters, state }, payload) {
      return this.dispatch('get', {
        path: 'v1/themes?include=mailingTypes',
        ...payload
      }).then((res) => {

        const data = deepGet(res, 'body.data')
        if (data) {

          const models = data.map((theme) => {
            commit('addThemeToCollection', { themeData: theme })
            return getters.getById(theme.id)
          })

          commit('addThemeToFilteredCollection', models)

          return { data: models, meta: res.body.meta }
        }
        return res
      })
    },
    getThemeById({ commit, getters }, id) {
      return this.dispatch('get', {
        path: 'v1/themes/' + id + '?include=mailingTypes',
      }).then((res) => {
        const themeData = deepGet(res, 'body.data')
        if (themeData) {
          commit('addThemeToCollection', { themeData })
          return getters.getById(id)
        }
        return res
      })
    },
    getUsage ({ commit }, payload) {
      return this.dispatch('get', {
        path: 'v1/themes/' + payload.id + '/usage',
        ...payload
      }).then((res) => {

        // Adjust numbers as early as possible
        res.body.data.forEach((account) => {
          account.mailingCount = convertNumberToLocale(account.mailingCount, this.getters.locale)
          account.articleCount = convertNumberToLocale(account.articleCount, this.getters.locale)
          commit('addUsageToCollection', account)
        })

        return res.body
      })
    },
    getCode ({ commit }, id) {
      return this.dispatch('get', {
        path: 'v1/themes/' + id + '/code'
      }).then((res) => {
        const code = deepGet(res, 'body.code')
        commit('updateCurrentCode', { code, id })
        return { code, id }
      })
    },
    getJson ({ commit }, id) {
      return this.dispatch('get', {
        path: 'v1/themes/' + id + '/json'
      }).then((res) => {
        const json = deepGet(res, 'body.json')
        commit('updateCurrentJson', { json, id })
        return { json, id }
      })
    },
    getCss ({ commit }, payload) {
      return this.dispatch('get', {
        path: 'v1/themes/' + payload.id + '/css/' + payload.filename
      }).then((res) => {
        const css = deepGet(res, 'body')
        commit('updateCurrentCss', {
          id: payload.id,
          css: css.code,
          name: payload.filename,
          onlyFrontend: css.onlyFrontend
        })
        return css
      })
    },
    getText ({ commit }, id) {
      return this.dispatch('get', {
        path: 'v1/themes/' + id + '/text'
      }).then((res) => {
        const text = deepGet(res, 'body')
        commit('updateCurrentText', {
          id: id,
          text: text.text
        })
        return text
      })
    },
    getCssFiles ({ commit }, payload) {
      return this.dispatch('get', {
        path: 'v1/themes/' + payload.id + '/cssfiles'
      }).then((res) => {
        const files = deepGet(res, 'body')
        commit('updateCssFiles', files)
        return files
      })
    },
    getFiles ({ commit }, payload) {
      let path = 'v1/themes/' + payload.id + '/files/'
      if (payload.directory) {
        path += payload.directory
      }
      return this.dispatch('get', { path })
        .then((res) => {
          const files = deepGet(res, 'body')
          commit('updateFiles', files)
          return files
        })
    },
    downloadFile ({ commit }, payload) {
      return this.dispatch('getWithOpts', {
        path: 'v1/themes/' + payload.id + '/files/download/' + payload.file,
        opts: { responseType: 'arraybuffer' }
      })
    },
    create({ commit, getters }, model) {
      return this.dispatch('post', {
        path: 'v1/themes',
        data: model.getJSON()
      }).then((res) => {

        commit('addThemeToCollection', { themeData: res.body && res.body.data })

        return getters.getById(res.body && res.body.data.id)
      })
    },
    createFolder({ commit, getters }, payload) {
      return this.dispatch('post', {
        path: 'v1/themes/' + payload.id + '/files/',
        data: payload
      })
    },
    createCssFile({ commit, getters, state }, payload) {
      return this.dispatch('post', {
        path: 'v1/themes/' + payload.id + '/css/',
        data: payload
      }).then((res) => {
        const files = deepGet(res, 'body')
        commit('appendCssFiles', files)
        commit('updateThemeCss', { themeId: payload.id, files: state.cssFiles })
        return files
      })
    },
    uploadFile({ commit, getters }, payload) {
      return this.dispatch('post', {
        path: 'v1/themes/' + payload.id + '/files',
        data: payload.formData
      })
    },
    update({ commit, getters }, model) {
      return this.dispatch('put', {
        path: 'v1/themes/' + model.id,
        data: model.getJSON()
      }).then((res) => {

        commit('addThemeToCollection', { themeData: res.body && res.body.data })

        return getters.getById(res.body && res.body.data.id)
      })
    },
    updateCode(store, payload) {
      return this.dispatch('put', {
        path: 'v1/themes/' + payload.id + '/code',
        data: payload
      })
    },
    updateJson(store, payload) {
      return this.dispatch('put', {
        path: 'v1/themes/' + payload.id + '/json',
        data: payload
      })
    },
    updateCss(store, payload) {
      return this.dispatch('put', {
        path: 'v1/themes/' + payload.id + '/css/' + payload.name,
        data: payload
      })
    },
    updateText(store, payload) {
      return this.dispatch('put', {
        path: 'v1/themes/' + payload.id + '/text',
        data: payload
      })
    },
    deleteTheme({ commit }, id) {
      return this.dispatch('del', {
        path: 'v1/themes/' + id
      }).then((res) => {

        commit('removeThemeFromCollection', id)

        return res
      })
    },
    deleteFile({ commit }, payload) {
      let path = 'v1/themes/' + payload.id + '/files'
      if (payload.directory) {
        path += payload.directory
      }
      return this.dispatch('del', { path })
    },
    deleteCssFile({ commit }, payload) {
      return this.dispatch('del', {
        path: 'v1/themes/' + payload.id + '/css/' + payload.name
      })
    },
    sync({ commit, getters, state }) {
      return this.dispatch('get', {
        path: 'v1/themes/sync'
      }).then((res) => {

        return res
      })
    }
  },
  mutations: {
    addThemeToCollection(state, { themeData, opts }) {
      addToCollection(this, state.themes, themeData, Theme, opts)
    },
    addUsageToCollection(state, data) {
      addToCollection(this, state.usage, data)
    },
    addThemeToFilteredCollection(state, data) {
      state.filteredCollection = state.filteredCollection.concat(data)
    },
    removeThemeFromCollection(state, themeId) {
      const index = state.themes.findIndex(el => el.id === themeId)

      if (index === -1) {
        console.warn(`Could not find/delete theme ${themeId}`)
        return
      }

      state.themes.splice(index, 1)
    },
    clearCollection(state) {
      state.themes = []
    },
    clearFilteredCollection(state) {
      state.filteredCollection = []
    },
    clearUsage(state) {
      state.usage = []
    },
    updateCurrent (state, theme) {
      state.current = theme
    },
    updateCurrentCode (state, code) {
      state.currentCode = code
    },
    updateCurrentCss (state, css) {
      state.currentCss = css
    },
    updateCurrentText (state, text) {
      state.currentText = text
    },
    resetCurrent (state) {
      state.current = new Theme()
    },
    resetCurrentCode (state) {
      state.currentCode = {...initialCodeState}
    },
    updateCurrentJson (state, json) {
      state.currentJson = json
    },
    resetCurrentCss (state) {
      state.currentCss = {...initialCssState}
    },
    resetCurrentText (state) {
      state.currentText = {...initialTextState}
    },
    updateFiles (state, files) {
      state.files = files
    },
    updateCssFiles (state, files) {
      state.cssFiles = files
    },
    updateCurrentFile (state, file) {
      state.currentFile = file
    },
    updateThemeCss (state, {themeId, files}) {
      const theme = state.themes.find(el => el.id === themeId)
      const currentCss = this.getters['themes/getCurrentCss']

      theme.css = files.map((el) => {
        return {
          name: el.name,
          onlyFrontend: currentCss.onlyFrontend
        }
      })
    },
    appendCssFiles (state, files) {
      state.cssFiles = state.cssFiles.concat(files)
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/theme/store.js